import { type Ref } from 'react';
import { cn } from '@/lib/utils';
import DynamicImageProvider from '@/providers/dynamic-image';
import type { ShopperProducts, ShopperSearch } from '@/scapi';
import { ProductCard, ProductCardSkeleton } from '../../molecules';
import type { ProductCardItem } from '../../../lib/product-card-adapter';

export type ProductGridItem = ShopperProducts.schemas['Product'] | ShopperSearch.schemas['ProductSearchHit'];

// Critical tiles: one DIS width so LCP gets a single preload, not a full picture srcset.
const CRITICAL_IMAGE_WIDTHS = [320];
// 2-col below lg, 3-col from lg. Width-only so DIS keeps the source aspect ratio.
const PRODUCT_GRID_IMAGE_WIDTHS = { base: '40vw', lg: '33.333vw' };

export type ProductGridProps = {
    data?: ProductGridItem[];
    critical?: ProductGridItem[];
    nonCritical?: ProductGridItem[];
    appended?: ProductGridItem[];
    isLoading?: boolean;
    onProductClick?: (product: ProductGridItem) => void;
    getProductUrl?: (product: ProductGridItem) => string;
    firstNewItemIndex?: number | null;
    firstNewItemRef?: Ref<HTMLElement>;
    newItemsStartIndex?: number | null;
    skeletonCount?: number;
    className?: string;
};

const productKey = (product: ProductGridItem) => String('productId' in product ? product.productId : product.id);

export const ProductGrid = ({
    data = [],
    critical,
    nonCritical = [],
    appended = [],
    isLoading = false,
    onProductClick,
    getProductUrl,
    firstNewItemIndex,
    firstNewItemRef,
    newItemsStartIndex,
    skeletonCount = 0,
    className,
}: ProductGridProps) => {
    const criticalItems = critical ?? data;
    const belowFoldItems = [...nonCritical, ...appended];
    const appendedStartIndex = criticalItems.length + nonCritical.length;

    const renderCard = (product: ProductGridItem, index: number, { priority = false } = {}) => (
        <ProductCard
            key={productKey(product)}
            product={product as ProductCardItem}
            priority={priority}
            onProductClick={onProductClick}
            getProductUrl={getProductUrl}
            ref={index === firstNewItemIndex ? firstNewItemRef : undefined}
            className={
                newItemsStartIndex != null && index >= newItemsStartIndex
                    ? 'motion-safe:animate-in motion-safe:fade-in motion-safe:duration-200'
                    : undefined
            }
        />
    );

    return (
        <section className={cn('w-full', className)}>
            <div
                data-slot="product-grid"
                data-testid="product-grid"
                className="grid grid-cols-2 border-l border-t border-snk-greige bg-snk-white lg:grid-cols-3"
                aria-busy={isLoading}>
                {criticalItems.length > 0 && (
                    <DynamicImageProvider value={{ widths: CRITICAL_IMAGE_WIDTHS }}>
                        {criticalItems.map((product, index) => renderCard(product, index, { priority: index === 0 }))}
                    </DynamicImageProvider>
                )}
                {belowFoldItems.length > 0 && (
                    <DynamicImageProvider value={{ widths: PRODUCT_GRID_IMAGE_WIDTHS }}>
                        {belowFoldItems.map((product, index) => renderCard(product, appendedStartIndex + index))}
                    </DynamicImageProvider>
                )}
                {Array.from({ length: skeletonCount }, (_, index) => (
                    <ProductCardSkeleton key={`product-card-skeleton-${index}`} />
                ))}
            </div>
        </section>
    );
};
